<template>
  <div class="jiffy-services-preview" :class="`jiffy-services-preview--design-${design}`">
    <div v-if="headerText" class="jiffy-services-preview__title">{{ headerText }}</div>
    <div class="jiffy-services-preview__arrows-wrapper">
      <div
        ref="scrollWrapper"
        class="jiffy-services-preview__scroll-wrapper"
        @scroll="handleSliderScroll()"
      >
        <div class="jiffy-services-preview__previews-wrapper">
          <JiffyPromotionTransfers
            v-if="showTransfersStoreCreditBanner"
            ref="promotionTransfers"
            :spend-threshold="50"
            :credit-value="20"
            :open-in-new-tab="openInNewTab"
          />
          <JiffyPromotionBlanks
            v-if="showBlanksFreeShippingBanner"
            ref="promotionBlanks"
            :spend-threshold="50"
            :open-in-new-tab="openInNewTab"
          />
          <JiffyServicePreview
            v-for="service in mergedServices"
            ref="services"
            :key="service.key"
            :service="service"
            :mixpanel-event="mixpanelEvent"
            :mixpanel-data="JSON.stringify({ page: 'cart' })"
            :source="source"
            :design="design"
            :horizontal="mergedServices.length === 1"
            :show-from-price="service.show_from_price"
            :show-product="isLargeUpsell"
            :open-in-new-tab="openInNewTab"
          />
        </div>
      </div>

      <button
        v-if="!isLargeUpsell"
        class="jiffy-services-preview__arrow jiffy-services-preview__arrow--left"
        aria-label="Slide left"
        :disabled="disableArrow('left')"
        data-direction="left"
        type="button"
        @click="handleArrowClick('left')"
      >
        <UseSvg icon="arrow-left-tiny" />
      </button>

      <button
        v-if="!isLargeUpsell"
        class="jiffy-services-preview__arrow jiffy-services-preview__arrow--right"
        aria-label="Slide right"
        data-direction="right"
        type="button"
        :disabled="disableArrow('right')"
        @click="handleArrowClick('right')"
      >
        <UseSvg icon="arrow-right-tiny" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { initMixpanelEvents } from "jiffy-analytics";
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "common/vue/components/UseSvg.vue";
import JiffyServicePreview from "./JiffyServicePreview.vue";
import JiffyPromotionTransfers from "./JiffyPromotionTransfers.vue";
import JiffyPromotionBlanks from "./JiffyPromotionBlanks.vue";

const buildPreviewUrl = path =>
  remoteImageStaticUrl(path, { ixlib: "rb-0.3.5", h: 100, w: 100, dpr: 2, q: 30 });

export default {
  name: "JiffyServicesPreview",
  components: { JiffyPromotionBlanks, JiffyPromotionTransfers, UseSvg, JiffyServicePreview },
  props: {
    services: {
      type: Object,
      required: true
    },
    design: {
      type: String,
      required: true
    },
    mixpanelEvent: {
      type: String,
      required: true
    },
    source: {
      type: String,
      required: false
    },
    headerText: {
      type: String,
      default: ""
    },
    openInNewTab: {
      type: Boolean,
      default: true
    },
    componentId: {
      type: String,
      default: "jiffy-services-preview"
    }
  },
  // eslint-disable-next-line max-lines-per-function
  data() {
    return {
      hasArrows: false,
      position: "left",
      transfers: {
        title: "Fabric Transfers (DTF)",
        preview: buildPreviewUrl("gen/jiffy-services-preview/transfers.png"),
        icon: "jiffy-transfers-inline",
        page_url: "/",
        cta: "Upload Image",
        additional_mixpanel_event: "transfers_cta_click",
        business_line_promo_type: "Transfers",
        promotion_name: "fabric_transfers",
        price_from: "$0",
        price_per: "$0",
        price_per_label: "per sq.\ninch",
        service: "transfers"
      },
      made: {
        title: "Custom Tees & Sweatshirts",
        preview: buildPreviewUrl("gen/jiffy-services-preview/made.png"),
        icon: "jiffy-made-inline",
        page_url: "/",
        cta: "Upload Image",
        business_line_promo_type: "Made",
        promotion_name: "custom_tees_sweatshirts",
        price_from: "$0",
        price_per: "$0",
        price_per_label: "all-in\nper shirt",
        service: "made",
        additional_mixpanel_event: "print_cta_click"
      },
      stickers: {
        title: "Die-Cut Vinyl Stickers",
        preview: buildPreviewUrl("gen/jiffy-services-preview/stickers.png"),
        icon: "jiffy-stickers-inline",
        page_url: "/",
        cta: "Add Stickers",
        additional_mixpanel_event: "stickers_cta_click",
        business_line_promo_type: "Stickers",
        promotion_name: "die_cut_vinyl_stickers",
        price_from: "$0",
        price_per: "$0",
        price_per_label: "per sq.\ninch",
        service: "stickers"
      },
      blanks: {
        title: "Shirts",
        preview: buildPreviewUrl("gen/jiffy-services-preview/blanks-no-logo.jpg"),
        icon: "jiffy-shirts-inline",
        page_url: "/",
        cta: "Add to cart",
        additional_mixpanel_event: "blanks_cta_click",
        business_line_promo_type: "Blanks",
        promotion_name: "shirts",
        price_per: "$0",
        price_from: "$0",
        service: "blanks"
      }
    };
  },
  computed: {
    ...mapGetters(["experiments", "showTransfersStoreCreditBanner"]),
    ...mapGetters("shipping", ["showBlanksFreeShippingBanner"]),
    preparedServices() {
      return Object.values(this.services).sort((serviceA, serviceB) => {
        // transfers always first
        if (serviceA.service === "transfers") return -1;
        if (serviceB.service === "transfers") return 1;
        // jiffy made is always after transfers
        if (serviceA.service === "made") return -1;
        if (serviceB.service === "made") return 1;
        return 0;
      });
    },
    mergedServices() {
      const mergedServices = this.preparedServices.map(service => {
        const { service: key } = service;
        return {
          ...(key in this ? this[key] : this.blanks),
          ...service
        };
      });

      return objNamesToSnakeCase(mergedServices);
    },
    isLargeUpsell() {
      return this.design === "large";
    }
  },
  mounted() {
    initMixpanelEvents(".jiffy-services-preview");

    new ResizeObserver(() => {
      window.requestAnimationFrame(() => this.outputSize());
    }).observe(this.$refs.scrollWrapper);
  },
  methods: {
    outputSize() {
      const { clientWidth, scrollWidth } = this.$refs.scrollWrapper;

      this.hasArrows = scrollWidth > clientWidth;
    },
    handleArrowClick(direction, overscrollValue = 50) {
      const { scrollLeft, clientWidth } = this.$refs.scrollWrapper;
      const scrollRight = scrollLeft + clientWidth;
      let elementHiddenLeft;
      let elementHiddenRight;

      [this.$refs.promotionBlanks, this.$refs.promotionTransfers, ...this.$refs.services].forEach(
        service => {
          const element = service?.$el;
          const elementLeft = element?.offsetLeft || 0;
          const elementRight = elementLeft + (element?.offsetWidth || 0);

          if (elementLeft < scrollLeft) {
            elementHiddenLeft = element;
          }

          if (!elementHiddenRight && elementRight > scrollRight) {
            elementHiddenRight = element;
          }
        }
      );

      this.$refs.scrollWrapper.scrollTo({
        left:
          direction === "left"
            ? (elementHiddenLeft?.offsetLeft || 0) - overscrollValue
            : (elementHiddenRight?.offsetLeft || 0) - overscrollValue,
        behavior: "smooth"
      });
    },
    handleSliderScroll() {
      const { scrollWidth, scrollLeft, clientWidth } = this.$refs.scrollWrapper;

      if (scrollLeft === 0) {
        this.position = "left";
        // -1 to account for rounding errors
      } else if (scrollLeft + clientWidth >= scrollWidth - 1) {
        this.position = "right";
      } else {
        this.position = "center";
      }
    },
    disableArrow(direction) {
      return this.position === direction || !this.hasArrows;
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.jiffy-services-preview {
  $this: &;

  background-color: $section-grey;
  padding: 8px;
  border-radius: 4px;

  &--design-pdp {
    #{$this}__previews-wrapper {
      grid-gap: 9px;
    }
  }

  &--design-cart-variant-1,
  &--design-cart-variant-non-dtf-catalog-products {
    margin-bottom: 16px;
    padding-right: 16px;
  }

  &__title {
    display: inline-block;
    background-color: $green-emerald-small-light;
    color: $white;
    font-size: 14px;
    line-height: 16px;
    padding: 6px 12px;
    margin-bottom: 8px;
    border-radius: 4px;
    font-weight: bold;
  }

  &__scroll-wrapper {
    @include hidden-scollbar;

    overscroll-behavior: none;
    overflow: auto;
    margin: 0 -8px;
  }

  &__previews-wrapper {
    display: flex;
    flex-wrap: nowrap;
    min-width: 100%;
    gap: 10px;
    padding: 0 8px;
  }

  &__arrows-wrapper {
    position: relative;
  }

  &__arrow {
    @include svg-wrapper;

    display: none;
    align-items: center;
    justify-content: space-around;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    border: 1px solid $gray-border;
    border-radius: 50%;
    background: $white;
    box-shadow: 0 4px 4px rgba($gray-text, 0.15);
    z-index: 1;
    padding: 12px;

    @include with-tablet {
      display: flex;
    }

    &:hover {
      box-shadow: 0 4px 4px rgba($gray-text, 0.4);
    }

    &:disabled {
      opacity: 0;
      pointer-events: none;
    }

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }
  }
}
</style>
